import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version — incident-first-aid
export const incidentFirstAidMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-ifa-1',
    text: {
      en: 'You are first at the scene of a road accident. What is the first thing you should do?',
      nl: 'U bent als eerste bij een verkeersongeval. Wat moet u als eerste doen?',
      ar: 'أنت أول من يصل إلى موقع حادث مروري. ما أول ما يجب أن تفعله؟',
    },
    options: [
      {
        id: 'q-ifa-1o1',
        text: {
          en: 'Move the injured to the side of the road',
          nl: 'De gewonden naar de kant van de weg brengen',
          ar: 'نقل المصابين إلى جانب الطريق',
        },
      },
      {
        id: 'q-ifa-1o2',
        text: {
          en: 'Make sure the scene is safe (switch off engines, warn traffic) and call emergency services',
          nl: 'Ervoor zorgen dat de plaats veilig is (motoren uitzetten, verkeer waarschuwen) en de hulpdiensten bellen',
          ar: 'التأكد من سلامة الموقع (إطفاء المحركات، تحذير المرور) والاتصال بخدمات الطوارئ',
        },
      },
      {
        id: 'q-ifa-1o3',
        text: {
          en: 'Leave immediately to get help',
          nl: 'Onmiddellijk vertrekken om hulp te halen',
          ar: 'المغادرة فورا لطلب المساعدة',
        },
      },
    ],
    correctAnswerId: 'q-ifa-1o2',
    explanation: {
      en: 'First make the scene safe (e.g. switch off ignitions, warn other traffic). Then call emergency services and give first aid if trained.',
      nl: 'Zorg eerst voor een veilige situatie (bijv. contact uitzetten, ander verkeer waarschuwen). Bel daarna de hulpdiensten en verleen eerste hulp indien u daartoe bent opgeleid.',
      ar: 'أولا اجعل الموقع آمنا (مثل إطفاء الإشعال وتحذير المرور). ثم اتصل بخدمات الطوارئ وقدم الإسعافات الأولية إذا كنت مؤهلا.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-2',
    text: {
      en: 'What should you do if someone is unconscious and not breathing normally at an accident?',
      nl: 'Wat moet u doen als iemand bij een ongeval buiten bewustzijn is en niet normaal ademt?',
      ar: 'ماذا تفعل إذا كان شخص فاقدا للوعي ولا يتنفس بشكل طبيعي عند حادث؟',
    },
    options: [
      {
        id: 'q-ifa-2o1',
        text: {
          en: 'Leave them and wait for ambulance',
          nl: 'Hem/haar laten liggen en wachten op de ambulance',
          ar: 'تركه والانتظار حتى تصل الإسعاف',
        },
      },
      {
        id: 'q-ifa-2o2',
        text: {
          en: 'Call emergency services and start CPR if you are trained',
          nl: 'De hulpdiensten bellen en reanimatie starten indien u daartoe bent opgeleid',
          ar: 'الاتصال بخدمات الطوارئ وبدء الإنعاش القلبي الرئوي إذا كنت مؤهلا',
        },
      },
      {
        id: 'q-ifa-2o3',
        text: {
          en: 'Give them water',
          nl: 'Hem/haar water geven',
          ar: 'إعطاؤه ماء',
        },
      },
    ],
    correctAnswerId: 'q-ifa-2o2',
    explanation: {
      en: 'Call emergency services immediately. If they are not breathing normally and you are trained, start CPR. If an AED is available, use it.',
      nl: 'Bel onmiddellijk de hulpdiensten. Als hij/zij niet normaal ademt en u bent opgeleid, start dan met reanimatie. Gebruik een AED indien beschikbaar.',
      ar: 'اتصل بخدمات الطوارئ فورا. إذا لم يكن التنفس طبيعيا وأنت مؤهل، ابدأ الإنعاش القلبي الرئوي. إذا وجد جهاز صدمات، استخدمه.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-3',
    text: {
      en: 'You are involved in an accident. What information should you exchange with the other driver?',
      nl: 'U bent bij een ongeval betrokken. Welke gegevens moet u met de andere bestuurder uitwisselen?',
      ar: 'أنت متورط في حادث. ما المعلومات التي يجب تبادلها مع السائق الآخر؟',
    },
    options: [
      {
        id: 'q-ifa-3o1',
        text: {
          en: 'Only names',
          nl: 'Alleen namen',
          ar: 'الأسماء فقط',
        },
      },
      {
        id: 'q-ifa-3o2',
        text: {
          en: 'Names, addresses, vehicle registration, and insurance details',
          nl: 'Namen, adressen, kenteken en verzekeringsgegevens',
          ar: 'الأسماء والعناوين وتسجيل المركبة وتفاصيل التأمين',
        },
      },
      {
        id: 'q-ifa-3o3',
        text: {
          en: 'Only phone numbers',
          nl: 'Alleen telefoonnummers',
          ar: 'أرقام الهواتف فقط',
        },
      },
    ],
    correctAnswerId: 'q-ifa-3o2',
    explanation: {
      en: 'Exchange names, addresses, vehicle registration numbers, and insurance details. Report the accident to the police if required.',
      nl: 'Wissel namen, adressen, kentekens en verzekeringsgegevens uit. Meld het ongeval bij de politie indien verplicht.',
      ar: 'تبادل الأسماء والعناوين وأرقام تسجيل المركبات وتفاصيل التأمين. أبلغ الشرطة عن الحادث إذا كان ذلك مطلوبا.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-4',
    text: {
      en: 'When should you use a warning triangle at an accident?',
      nl: 'Wanneer moet u een gevarendriehoek bij een ongeval gebruiken?',
      ar: 'متى يجب استخدام مثلث التحذير عند حادث؟',
    },
    options: [
      {
        id: 'q-ifa-4o1',
        text: {
          en: 'Only on motorways',
          nl: 'Alleen op autosnelwegen',
          ar: 'على الطرق السريعة فقط',
        },
      },
      {
        id: 'q-ifa-4o2',
        text: {
          en: 'To warn other traffic; place it at a safe distance on the same side of the road',
          nl: 'Om ander verkeer te waarschuwen; plaats deze op veilige afstand aan dezelfde zijde van de weg',
          ar: 'لتحذير المرور الآخر؛ ضعه على مسافة آمنة في نفس جانب الطريق',
        },
      },
      {
        id: 'q-ifa-4o3',
        text: {
          en: 'Only at night',
          nl: "Alleen 's nachts",
          ar: 'ليلا فقط',
        },
      },
    ],
    correctAnswerId: 'q-ifa-4o2',
    explanation: {
      en: 'Place a warning triangle well behind the vehicle to warn approaching traffic. Use hazard lights. On motorways, stay behind the barrier if possible.',
      nl: 'Plaats een gevarendriehoek ruim achter het voertuig om naderend verkeer te waarschuwen. Gebruik waarschuwingslichten. Blijf op autosnelwegen indien mogelijk achter de vangrail.',
      ar: 'ضع مثلث التحذير بعيدا خلف المركبة لتحذير المرور القادم. استخدم أضواء التحذير. على الطرق السريعة ابق خلف الحاجز إن أمكن.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-5',
    text: {
      en: 'What should you do if someone is bleeding heavily from a wound?',
      nl: 'Wat moet u doen als iemand hevig bloedt uit een wond?',
      ar: 'ماذا تفعل إذا كان شخص ينزف بغزارة من جرح؟',
    },
    options: [
      {
        id: 'q-ifa-5o1',
        text: {
          en: 'Leave it; do not touch',
          nl: 'Het laten; niet aanraken',
          ar: 'تركه دون لمس',
        },
      },
      {
        id: 'q-ifa-5o2',
        text: {
          en: 'Apply direct pressure with a clean cloth or dressing; call for help',
          nl: 'Directe druk uitoefenen met een schone doek of verband; om hulp roepen',
          ar: 'الضغط المباشر بقطعة قماش أو ضمادة نظيفة؛ طلب المساعدة',
        },
      },
      {
        id: 'q-ifa-5o3',
        text: {
          en: 'Pour water on it',
          nl: 'Er water overheen gieten',
          ar: 'سكب الماء عليه',
        },
      },
    ],
    correctAnswerId: 'q-ifa-5o2',
    explanation: {
      en: 'Apply direct pressure to the wound with a clean cloth or dressing to control bleeding. Call for emergency help.',
      nl: 'Oefen directe druk op de wond uit met een schone doek of verband om de bloeding te stelpen. Roep de hulpdiensten.',
      ar: 'اضغط مباشرة على الجرح بقطعة قماش أو ضمادة نظيفة لوقف النزيف. اطلب المساعدة الطارئة.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-6',
    text: {
      en: 'You break down on the motorway. What should you do?',
      nl: 'U heeft pech op de autosnelweg. Wat moet u doen?',
      ar: 'تتعطل مركبتك على الطريق السريع. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-ifa-6o1',
        text: {
          en: 'Stay in the car and wait',
          nl: 'In de auto blijven en wachten',
          ar: 'البقاء في السيارة والانتظار',
        },
      },
      {
        id: 'q-ifa-6o2',
        text: {
          en: 'Move to a safe place (e.g. behind barrier), use hazard lights, and call for help',
          nl: 'Naar een veilige plek gaan (bijv. achter de vangrail), waarschuwingslichten gebruiken en om hulp bellen',
          ar: 'الانتقال إلى مكان آمن (مثل خلف الحاجز) واستخدام أضواء التحذير والاتصال طلب المساعدة',
        },
      },
      {
        id: 'q-ifa-6o3',
        text: {
          en: 'Walk to the next exit',
          nl: 'Te voet naar de volgende afrit',
          ar: 'المشي إلى أقرب مخرج',
        },
      },
    ],
    correctAnswerId: 'q-ifa-6o2',
    explanation: {
      en: 'Get everyone to a safe place (e.g. behind the barrier). Use hazard lights. Call for help. Do not stay in the car on the carriageway.',
      nl: 'Breng iedereen naar een veilige plek (bijv. achter de vangrail). Gebruik waarschuwingslichten. Bel om hulp. Blijf niet in de auto op de rijbaan.',
      ar: 'انقل الجميع إلى مكان آمن (مثل خلف الحاجز). استخدم أضواء التحذير. اطلب المساعدة. لا تبق في السيارة على مسار المرور.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-7',
    text: {
      en: 'What should you do if you suspect someone has a spinal injury?',
      nl: 'Wat moet u doen als u vermoedt dat iemand een wervelletsel heeft?',
      ar: 'ماذا تفعل إذا اشتبهت أن لدى شخص إصابة في العمود الفقري؟',
    },
    options: [
      {
        id: 'q-ifa-7o1',
        text: {
          en: 'Move them to a comfortable position',
          nl: 'Hem/haar in een comfortabele houding brengen',
          ar: 'نقله إلى وضع مريح',
        },
      },
      {
        id: 'q-ifa-7o2',
        text: {
          en: 'Keep them still; support the head and neck; call emergency services',
          nl: 'Hem/haar stil houden; hoofd en nek ondersteunen; hulpdiensten bellen',
          ar: 'إبقاؤه ساكنا؛ تثبيت الرأس والرقبة؛ الاتصال بخدمات الطوارئ',
        },
      },
      {
        id: 'q-ifa-7o3',
        text: {
          en: 'Sit them up',
          nl: 'Hem/haar rechtop zetten',
          ar: 'إجلاسه',
        },
      },
    ],
    correctAnswerId: 'q-ifa-7o2',
    explanation: {
      en: 'Do not move them unless in immediate danger. Support the head and neck to keep the spine in line. Call emergency services.',
      nl: 'Verplaats hem/haar niet tenzij er direct gevaar is. Ondersteun hoofd en nek om de wervelkolom in lijn te houden. Bel de hulpdiensten.',
      ar: 'لا تحركه إلا إذا كان في خطر مباشر. ثبت الرأس والرقبة لإبقاء العمود الفقري مستقيما. اتصل بخدمات الطوارئ.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-8',
    text: {
      en: 'When must you report an accident to the police?',
      nl: 'Wanneer moet u een ongeval bij de politie melden?',
      ar: 'متى يجب الإبلاغ عن حادث إلى الشرطة؟',
    },
    options: [
      {
        id: 'q-ifa-8o1',
        text: {
          en: 'Only if someone is injured',
          nl: 'Alleen als iemand gewond is',
          ar: 'فقط إذا أصيب أحد',
        },
      },
      {
        id: 'q-ifa-8o2',
        text: {
          en: 'When someone is injured, or when there is significant damage or other reportable circumstances (rules vary by country)',
          nl: 'Wanneer iemand gewond is, of bij aanzienlijke schade of andere meldingsplichtige omstandigheden (regels verschillen per land)',
          ar: 'عند إصابة شخص، أو عند أضرار جسيمة أو ظروف أخرى يجب الإبلاغ عنها (تختلف القواعد حسب البلد)',
        },
      },
      {
        id: 'q-ifa-8o3',
        text: {
          en: 'Never; insurance handles it',
          nl: 'Nooit; de verzekering regelt het',
          ar: 'أبدا؛ التأمين يتولى الأمر',
        },
      },
    ],
    correctAnswerId: 'q-ifa-8o2',
    explanation: {
      en: 'You must report an accident to the police when someone is injured, or when required by law (e.g. hit-and-run, significant damage).',
      nl: 'U moet een ongeval melden bij de politie wanneer iemand gewond is, of wanneer de wet dit voorschrijft (bijv. doorrijden na ongeval, aanzienlijke schade).',
      ar: 'يجب الإبلاغ عن الحادث للشرطة عند إصابة أحد، أو عندما يقتضي القانون ذلك (مثل هروب من موقع الحادث أو أضرار كبيرة).',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-9',
    text: {
      en: 'What is the "primary survey" in first aid?',
      nl: "Wat is de 'primaire inspectie' bij eerste hulp?",
      ar: 'ما المقصود بـ«الفحص الأولي» في الإسعافات الأولية؟',
    },
    options: [
      {
        id: 'q-ifa-9o1',
        text: {
          en: 'Checking the car for damage',
          nl: 'De auto controleren op schade',
          ar: 'فحص السيارة عن الأضرار',
        },
      },
      {
        id: 'q-ifa-9o2',
        text: {
          en: 'Checking Danger, Response, Airway, Breathing, Circulation (DRABC)',
          nl: 'Controleren op Gevaar, Reactie, Luchtweg, Ademhaling, Circulatie (DRABC)',
          ar: 'التحقق من الخطر والاستجابة والمجرى الهوائي والتنفس والدورة الدموية (DRABC)',
        },
      },
      {
        id: 'q-ifa-9o3',
        text: {
          en: 'Taking photos of the scene',
          nl: "Foto's maken van de plaats van het ongeval",
          ar: 'التقاط صور للموقع',
        },
      },
    ],
    correctAnswerId: 'q-ifa-9o2',
    explanation: {
      en: 'Primary survey: Danger (make safe), Response (are they conscious?), Airway (clear?), Breathing (normal?), Circulation (severe bleeding?).',
      nl: 'Primaire inspectie: Gevaar (veilig maken), Reactie (bij bewustzijn?), Luchtweg (vrij?), Ademhaling (normaal?), Circulatie (ernstige bloeding?).',
      ar: 'الفحص الأولي: الخطر (جعل الموقع آمنا)، الاستجابة (هل هو واع؟)، المجرى الهوائي (هل هو مفتوح؟)، التنفس (هل هو طبيعي؟)، الدورة الدموية (نزيف شديد؟).',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-10',
    text: {
      en: 'You come across an accident. Someone is trapped in a vehicle. What should you do?',
      nl: 'U komt bij een ongeval. Iemand zit bekneld in een voertuig. Wat moet u doen?',
      ar: 'تصادف حادثا. شخص محبوس داخل مركبة. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-ifa-10o1',
        text: {
          en: 'Pull them out immediately',
          nl: 'Hem/haar onmiddellijk eruit trekken',
          ar: 'سحبه فورا',
        },
      },
      {
        id: 'q-ifa-10o2',
        text: {
          en: 'Call emergency services; keep them still and calm; do not move them unless in immediate danger (e.g. fire)',
          nl: 'Hulpdiensten bellen; hem/haar stil en rustig houden; niet verplaatsen tenzij er direct gevaar is (bijv. brand)',
          ar: 'الاتصال بخدمات الطوارئ؛ إبقاؤه ساكنا وهادئا؛ عدم تحريكه إلا إذا كان في خطر مباشر (مثل حريق)',
        },
      },
      {
        id: 'q-ifa-10o3',
        text: {
          en: 'Leave them; wait for professionals',
          nl: 'Hem/haar laten; wachten op professionals',
          ar: 'تركه والانتظار حتى يصل المختصون',
        },
      },
    ],
    correctAnswerId: 'q-ifa-10o2',
    explanation: {
      en: 'Call emergency services. Keep the person still and calm. Do not move them unless there is immediate danger (e.g. fire).',
      nl: 'Bel de hulpdiensten. Houd de persoon stil en rustig. Verplaats hem/haar niet tenzij er direct gevaar is (bijv. brand).',
      ar: 'اتصل بخدمات الطوارئ. أبق المصاب ساكنا وهادئا. لا تحركه إلا إذا كان هناك خطر مباشر (مثل حريق).',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-11',
    text: {
      en: 'What should you do if a motorcyclist has come off their bike and is wearing a helmet?',
      nl: 'Wat moet u doen als een motorrijder van de motor is gevallen en een helm draagt?',
      ar: 'ماذا تفعل إذا سقط راكب دراجة نارية وكان يرتدي خوذة؟',
    },
    options: [
      {
        id: 'q-ifa-11o1',
        text: {
          en: 'Remove the helmet immediately',
          nl: 'De helm onmiddellijk afnemen',
          ar: 'خلع الخوذة فورا',
        },
      },
      {
        id: 'q-ifa-11o2',
        text: {
          en: 'Do not remove the helmet unless they are not breathing or you are trained; support head and call for help',
          nl: 'De helm niet afnemen tenzij hij/zij niet ademt of u bent opgeleid; hoofd ondersteunen en om hulp roepen',
          ar: 'عدم خلع الخوذة إلا إذا لم يكن يتنفس أو كنت مؤهلا؛ تثبيت الرأس وطلب المساعدة',
        },
      },
      {
        id: 'q-ifa-11o3',
        text: {
          en: 'Leave the helmet on and move them',
          nl: 'De helm laten zitten en hem/haar verplaatsen',
          ar: 'إبقاء الخوذة وتحريكه',
        },
      },
    ],
    correctAnswerId: 'q-ifa-11o2',
    explanation: {
      en: 'Do not remove the helmet unless necessary (e.g. to open airway or perform CPR) or you are trained. Support the head and call for help.',
      nl: 'Neem de helm niet af tenzij nodig (bijv. om de luchtweg vrij te maken of reanimatie uit te voeren) of u bent opgeleid. Ondersteun het hoofd en roep om hulp.',
      ar: 'لا تخلع الخوذة إلا عند الضرورة (مثل فتح المجرى الهوائي أو الإنعاش) أو إذا كنت مؤهلا. ثبت الرأس واطلب المساعدة.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-12',
    text: {
      en: 'What does "DRSABC" stand for in first aid?',
      nl: "Wat betekent 'DRSABC' bij eerste hulp?",
      ar: 'ماذا تعني اختصارات «DRSABC» في الإسعافات الأولية؟',
    },
    options: [
      {
        id: 'q-ifa-12o1',
        text: {
          en: 'Drive, Reverse, Stop, Accelerate, Brake, Clutch',
          nl: 'Rijden, Achteruit, Stoppen, Versnellen, Remmen, Koppeling',
          ar: 'القيادة، الرجوع للخلف، التوقف، التسارع، الكبح، القابض',
        },
      },
      {
        id: 'q-ifa-12o2',
        text: {
          en: 'Danger, Response, Send for help, Airway, Breathing, Circulation',
          nl: 'Gevaar, Reactie, Hulp inroepen, Luchtweg, Ademhaling, Circulatie',
          ar: 'الخطر، الاستجابة، طلب المساعدة، المجرى الهوائي، التنفس، الدورة الدموية',
        },
      },
      {
        id: 'q-ifa-12o3',
        text: {
          en: 'Document, Report, Sign, Alert, Brief, Call',
          nl: 'Documenteren, Rapporteren, Ondertekenen, Waarschuwen, Informeren, Bellen',
          ar: 'التوثيق، الإبلاغ، التوقيع، التنبيه، الإحاطة، الاتصال',
        },
      },
    ],
    correctAnswerId: 'q-ifa-12o2',
    explanation: {
      en: 'DRSABC: Danger (make safe), Response (check consciousness), Send for help, Airway (open?), Breathing (normal?), Circulation (bleeding?).',
      nl: 'DRSABC: Gevaar (veilig maken), Reactie (bewustzijn controleren), Hulp inroepen, Luchtweg (open?), Ademhaling (normaal?), Circulatie (bloeding?).',
      ar: 'DRSABC: الخطر (جعل الموقع آمنا)، الاستجابة (التحقق من الوعي)، طلب المساعدة، المجرى الهوائي (مفتوح؟)، التنفس (طبيعي؟)، الدورة الدموية (نزيف؟).',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-13',
    text: {
      en: 'You have an accident and your vehicle is blocking the road. What should you do?',
      nl: 'U heeft een ongeval en uw voertuig blokkeert de weg. Wat moet u doen?',
      ar: 'تعرضت لحادث ومركبتك تعيق الطريق. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-ifa-13o1',
        text: {
          en: 'Leave it and go home',
          nl: 'Het laten en naar huis gaan',
          ar: 'تركها والذهاب إلى المنزل',
        },
      },
      {
        id: 'q-ifa-13o2',
        text: {
          en: 'Warn other traffic (hazard lights, triangle if safe), move vehicle if possible and safe, call police if needed',
          nl: 'Ander verkeer waarschuwen (waarschuwingslichten, driehoek indien veilig), voertuig verplaatsen indien mogelijk en veilig, politie bellen indien nodig',
          ar: 'تحذير المرور الآخر (أضواء التحذير، المثلث إن أمكن بأمان)، نقل المركبة إن أمكن بأمان، الاتصال بالشرطة عند الحاجة',
        },
      },
      {
        id: 'q-ifa-13o3',
        text: {
          en: 'Wait for someone else to move it',
          nl: 'Wachten tot iemand anders het verplaatst',
          ar: 'الانتظار حتى ينقلها شخص آخر',
        },
      },
    ],
    correctAnswerId: 'q-ifa-13o2',
    explanation: {
      en: 'Warn other traffic. If the vehicle can be moved safely (e.g. no serious injury), move it off the road. Report the accident if required.',
      nl: 'Waarschuw ander verkeer. Als het voertuig veilig kan worden verplaatst (bijv. geen ernstig letsel), rijd het dan van de weg af. Meld het ongeval indien verplicht.',
      ar: 'حذر المرور الآخر. إذا أمكن نقل المركبة بأمان (مثل عدم وجود إصابات خطيرة)، انقلها خارج الطريق. أبلغ عن الحادث إذا لزم.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-14',
    text: {
      en: 'What should you do if someone is in shock after an accident?',
      nl: 'Wat moet u doen als iemand in shock is na een ongeval?',
      ar: 'ماذا تفعل إذا كان شخص في حالة صدمة بعد حادث؟',
    },
    options: [
      {
        id: 'q-ifa-14o1',
        text: {
          en: 'Give them food and drink',
          nl: 'Hem/haar eten en drinken geven',
          ar: 'إعطاؤه طعاما وماء',
        },
      },
      {
        id: 'q-ifa-14o2',
        text: {
          en: 'Keep them warm, lay them down if possible, raise legs slightly; call for help',
          nl: 'Hem/haar warm houden, indien mogelijk laten liggen, benen licht verhogen; om hulp roepen',
          ar: 'إبقاؤه دافئا، وإن أمكن جعله يستلقي ورفع الساقين قليلا؛ طلب المساعدة',
        },
      },
      {
        id: 'q-ifa-14o3',
        text: {
          en: 'Walk them around',
          nl: 'Hem/haar laten rondlopen',
          ar: 'إجباره على المشي',
        },
      },
    ],
    correctAnswerId: 'q-ifa-14o2',
    explanation: {
      en: 'Keep them warm and calm. Lay them down and raise their legs slightly if possible. Call for emergency help. Do not give food or drink.',
      nl: 'Houd hem/haar warm en rustig. Laat hem/haar liggen en verhoog de benen licht indien mogelijk. Roep de hulpdiensten. Geef geen eten of drinken.',
      ar: 'أبقه دافئا وهادئا. ألقه وأرفع ساقيه قليلا إن أمكن. اطلب المساعدة الطارئة. لا تعطه طعاما أو شرابا.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-15',
    text: {
      en: 'When should you move an injured person at an accident?',
      nl: 'Wanneer moet u een gewonde bij een ongeval verplaatsen?',
      ar: 'متى يجب نقل مصاب عند حادث؟',
    },
    options: [
      {
        id: 'q-ifa-15o1',
        text: {
          en: 'Always; get them to the side of the road',
          nl: 'Altijd; hem/haar naar de kant van de weg brengen',
          ar: 'دائما؛ نقله إلى جانب الطريق',
        },
      },
      {
        id: 'q-ifa-15o2',
        text: {
          en: 'Only if they are in immediate danger (e.g. fire, traffic) and it is safe to do so',
          nl: 'Alleen als er direct gevaar is (bijv. brand, verkeer) en het veilig kan',
          ar: 'فقط إذا كان في خطر مباشر (مثل حريق أو مرور) وكان النقل آمنا',
        },
      },
      {
        id: 'q-ifa-15o3',
        text: {
          en: 'Never',
          nl: 'Nooit',
          ar: 'أبدا',
        },
      },
    ],
    correctAnswerId: 'q-ifa-15o2',
    explanation: {
      en: 'Move an injured person only if they are in immediate danger (e.g. fire, vehicle at risk of collision) and you can do so without causing further harm.',
      nl: 'Verplaats een gewonde alleen als er direct gevaar is (bijv. brand, botsingsgevaar) en u dit kunt doen zonder extra schade.',
      ar: 'انقل المصاب فقط إذا كان في خطر مباشر (مثل حريق أو احتمال اصطدام) ويمكنك ذلك دون إلحاق أذى إضافي.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-16',
    text: {
      en: 'What should you do if you hit a dog or other animal?',
      nl: 'Wat moet u doen als u een hond of ander dier aanrijdt?',
      ar: 'ماذا تفعل إذا اصطدمت بكلب أو حيوان آخر؟',
    },
    options: [
      {
        id: 'q-ifa-16o1',
        text: {
          en: 'Drive away quickly',
          nl: 'Snel wegrijden',
          ar: 'المغادرة بسرعة',
        },
      },
      {
        id: 'q-ifa-16o2',
        text: {
          en: 'Stop if safe; check on the animal; report to police or owner if required',
          nl: 'Stoppen indien veilig; het dier controleren; indien nodig melden bij politie of eigenaar',
          ar: 'التوقف إن أمكن بأمان؛ فحص الحيوان؛ الإبلاغ للشرطة أو المالك إذا لزم',
        },
      },
      {
        id: 'q-ifa-16o3',
        text: {
          en: 'Nothing; animals are not your responsibility',
          nl: 'Niets; dieren zijn niet uw verantwoordelijkheid',
          ar: 'لا شيء؛ الحيوانات ليست من مسؤوليتك',
        },
      },
    ],
    correctAnswerId: 'q-ifa-16o2',
    explanation: {
      en: 'Stop if safe to do so. Check on the animal. You may need to report to the police or try to find the owner.',
      nl: 'Stop indien veilig. Controleer het dier. U moet mogelijk melden bij de politie of de eigenaar proberen te vinden.',
      ar: 'توقف إن أمكن بأمان. تفقد الحيوان. قد تحتاج للإبلاغ للشرطة أو البحث عن المالك.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-17',
    text: {
      en: 'What is the correct order of actions at an accident scene?',
      nl: 'Wat is de juiste volgorde van handelen bij een ongeval?',
      ar: 'ما الترتيب الصحيح للإجراءات في موقع الحادث؟',
    },
    options: [
      {
        id: 'q-ifa-17o1',
        text: {
          en: 'Give first aid, then make safe, then call for help',
          nl: 'Eerste hulp verlenen, daarna veilig maken, daarna hulp inroepen',
          ar: 'الإسعاف أولا، ثم جعل الموقع آمنا، ثم طلب المساعدة',
        },
      },
      {
        id: 'q-ifa-17o2',
        text: {
          en: 'Make the scene safe, call for help, then give first aid if trained',
          nl: 'De plaats veilig maken, hulp inroepen, daarna eerste hulp verlenen indien opgeleid',
          ar: 'جعل الموقع آمنا، طلب المساعدة، ثم تقديم الإسعافات الأولية إذا كنت مؤهلا',
        },
      },
      {
        id: 'q-ifa-17o3',
        text: {
          en: 'Call for help only; do nothing else',
          nl: 'Alleen om hulp roepen; verder niets doen',
          ar: 'طلب المساعدة فقط دون غير ذلك',
        },
      },
    ],
    correctAnswerId: 'q-ifa-17o2',
    explanation: {
      en: 'First make the scene safe (for you and others). Then call emergency services. Then give first aid if you are trained and it is safe.',
      nl: 'Maak eerst de plaats veilig (voor uzelf en anderen). Bel daarna de hulpdiensten. Verleen daarna eerste hulp indien u opgeleid bent en het veilig is.',
      ar: 'أولا اجعل الموقع آمنا (لك وللآخرين). ثم اتصل بخدمات الطوارئ. ثم قدم الإسعافات الأولية إذا كنت مؤهلا والوضع آمنا.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-18',
    text: {
      en: 'You are first at the scene. There are several injured people. What should you do?',
      nl: 'U bent als eerste ter plaatse. Er zijn meerdere gewonden. Wat moet u doen?',
      ar: 'أنت أول من يصل. هناك عدة مصابين. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-ifa-18o1',
        text: {
          en: 'Treat the first person you see',
          nl: 'De eerste persoon die u ziet behandelen',
          ar: 'معالجة أول شخص تراه',
        },
      },
      {
        id: 'q-ifa-18o2',
        text: {
          en: 'Call emergency services with clear details; then help those with life-threatening injuries first if trained',
          nl: 'Hulpdiensten bellen met duidelijke details; daarna indien opgeleid eerst helpen bij levensbedreigende verwondingen',
          ar: 'الاتصال بخدمات الطوارئ بتفاصيل واضحة؛ ثم مساعدة من لديهم إصابات مهددة للحياة أولا إذا كنت مؤهلا',
        },
      },
      {
        id: 'q-ifa-18o3',
        text: {
          en: 'Wait for the ambulance before doing anything',
          nl: 'Wachten op de ambulance voordat u iets doet',
          ar: 'الانتظار حتى تصل الإسعاف دون فعل شيء',
        },
      },
    ],
    correctAnswerId: 'q-ifa-18o2',
    explanation: {
      en: 'Call emergency services first and give a clear picture of the scene. Then prioritise those with life-threatening injuries if you are trained.',
      nl: 'Bel eerst de hulpdiensten en geef een duidelijk beeld van de situatie. Prioriteer daarna indien opgeleid personen met levensbedreigende verwondingen.',
      ar: 'اتصل بخدمات الطوارئ أولا وصف الوضع بوضوح. ثم أعط الأولوية لمن لديهم إصابات مهددة للحياة إذا كنت مؤهلا.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-19',
    text: {
      en: 'What should you do if a casualty is not breathing but has a pulse?',
      nl: 'Wat moet u doen als een slachtoffer niet ademt maar wel een pols heeft?',
      ar: 'ماذا تفعل إذا كان المصاب لا يتنفس لكن له نبض؟',
    },
    options: [
      {
        id: 'q-ifa-19o1',
        text: {
          en: 'Start CPR immediately',
          nl: 'Onmiddellijk met reanimatie beginnen',
          ar: 'بدء الإنعاش القلبي الرئوي فورا',
        },
      },
      {
        id: 'q-ifa-19o2',
        text: {
          en: 'Open the airway and give rescue breaths if trained; call for help',
          nl: 'Luchtweg openen en beademingen geven indien opgeleid; om hulp roepen',
          ar: 'فتح المجرى الهوائي وإعطاء نفخات إنقاذ إذا كنت مؤهلا؛ طلب المساعدة',
        },
      },
      {
        id: 'q-ifa-19o3',
        text: {
          en: 'Do nothing; wait for ambulance',
          nl: 'Niets doen; wachten op de ambulance',
          ar: 'عدم فعل شيء والانتظار حتى الإسعاف',
        },
      },
    ],
    correctAnswerId: 'q-ifa-19o2',
    explanation: {
      en: 'If they have a pulse but are not breathing, open the airway and give rescue breaths if you are trained. Call for help. If no pulse, start CPR.',
      nl: 'Als er een pols is maar niet wordt geademd, maak de luchtweg vrij en geef beademingen indien opgeleid. Roep om hulp. Bij geen pols: start reanimatie.',
      ar: 'إذا كان له نبض ولا يتنفس، افتح المجرى الهوائي وأعط نفخات إنقاذ إذا كنت مؤهلا. اطلب المساعدة. إذا لم يكن له نبض، ابدأ الإنعاش القلبي الرئوي.',
    },
    subject: 'Incident & First Aid',
  },
  {
    id: 'q-ifa-20',
    text: {
      en: 'After an accident, why should you not admit fault at the scene?',
      nl: 'Waarom moet u na een ongeval ter plaatse geen schuld erkennen?',
      ar: 'بعد حادث، لماذا لا يجب الاعتراف بالخطأ في الموقع؟',
    },
    options: [
      {
        id: 'q-ifa-20o1',
        text: {
          en: 'Because it is illegal',
          nl: 'Omdat het illegaal is',
          ar: 'لأنه غير قانوني',
        },
      },
      {
        id: 'q-ifa-20o2',
        text: {
          en: 'Fault is for insurers and possibly courts to determine; stick to facts and exchange details',
          nl: 'Schuld wordt door verzekeraars en eventueel door rechters vastgesteld; beperk u tot feiten en wissel gegevens uit',
          ar: 'تحديد الخطأ من مسؤولة شركات التأمين وربما المحاكم؛ التزم بالوقائع وتبادل البيانات',
        },
      },
      {
        id: 'q-ifa-20o3',
        text: {
          en: 'You should always admit fault',
          nl: 'U moet altijd schuld erkennen',
          ar: 'يجب أن تعترف بالخطأ دائما',
        },
      },
    ],
    correctAnswerId: 'q-ifa-20o2',
    explanation: {
      en: 'Do not admit fault at the scene. Exchange details and report facts. Fault is determined by insurers and, if needed, the courts.',
      nl: 'Erken ter plaatse geen schuld. Wissel gegevens uit en rapporteer feiten. Schuld wordt vastgesteld door verzekeraars en zo nodig door de rechter.',
      ar: 'لا تعترف بالخطأ في الموقع. تبادل البيانات واذكر الوقائع. يحدد الخطأ شركات التأمين وعند الحاجة القضاء.',
    },
    subject: 'Incident & First Aid',
  },
];
